import { BlobStore } from './blobStore.ts';
import { type KeyStore, openKeyStore } from './keyStore.ts';
import { ProjectRepo } from './projects.ts';

export interface Storage {
  keys: KeyStore;
  blobs: BlobStore;
  projects: ProjectRepo;
}

let current: Storage | null = null;

/** Opens storage once (OPFS probe, then IndexedDB fallback). */
export async function initStorage(keys?: KeyStore): Promise<Storage> {
  if (current && !keys) return current;
  const k = keys ?? (await openKeyStore());
  const blobs = new BlobStore(k);
  current = { keys: k, blobs, projects: new ProjectRepo(blobs) };
  return current;
}

export function storage(): Storage {
  if (!current) throw new Error('Storage is not ready yet.');
  return current;
}

/**
 * Asks the browser to keep our data (call from a user gesture after the first save).
 * Safari evicts script-writable storage of non-installed sites after 7 days without a visit.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function storageEstimate(): Promise<{ usage: number; quota: number; persisted: boolean } | null> {
  try {
    const e = await navigator.storage.estimate();
    return { usage: e.usage ?? 0, quota: e.quota ?? 0, persisted: (await navigator.storage.persisted?.()) ?? false };
  } catch {
    return null;
  }
}
